import React, { useState, useRef, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Tag } from '@/types/bookmark'
import { tagApi } from '../../../../shared/services/tagApi'

interface TagInputProps {
  value: Tag[]
  onChange: (tags: Tag[]) => void
  placeholder?: string
  maxTags?: number
  disabled?: boolean
}

const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  placeholder = '태그를 입력하세요...',
  maxTags = 10,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<Tag[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)

  // 태그 자동완성 조회
  const fetchSuggestions = async (query: string) => {
    if (query.length < 1) {
      setSuggestions([])
      return
    }

    try {
      const found = await tagApi.suggestions(query)
      // 이미 선택된 태그는 제외
      setSuggestions(
        (found as unknown as Tag[]).filter(
          tag => !value.some(selectedTag => selectedTag._id === tag._id)
        )
      )
    } catch (error) {
      console.error('태그 검색 실패:', error)
      setSuggestions([])
    }
  }

  // 입력값 변경 시 자동완성 조회
  useEffect(() => {
    const trimmedValue = inputValue.trim()
    if (trimmedValue) {
      fetchSuggestions(trimmedValue)
      setShowSuggestions(true)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
    setActiveSuggestionIndex(-1)
  }, [inputValue])

  // 태그 추가 함수
  const addTag = async (tagName: string) => {
    if (!tagName.trim() || value.length >= maxTags) {
      return
    }

    // 이미 추가된 태그인지 확인
    if (value.some(tag => tag.name.toLowerCase() === tagName.toLowerCase())) {
      setInputValue('')
      setShowSuggestions(false)
      return
    }

    try {
      // 서버에 태그 생성/조회 요청
      const newTag = await tagApi.create({ name: tagName.trim() })
      if (newTag) {
        onChange([...value, newTag as unknown as Tag])
        setInputValue('')
        setShowSuggestions(false)
      }
    } catch (error) {
      console.error('태그 생성 실패:', error)
    }
  }

  // 태그 제거
  const removeTag = (tagId: string) => {
    onChange(value.filter(tag => tag._id !== tagId))
  }

  // 키보드 이벤트 처리
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
          addTag(suggestions[activeSuggestionIndex].name)
        } else if (inputValue.trim()) {
          addTag(inputValue.trim())
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveSuggestionIndex(prev =>
          prev <= 0 ? suggestions.length - 1 : prev - 1
        )
        break
      case 'ArrowDown':
        e.preventDefault()
        setActiveSuggestionIndex(prev =>
          prev >= suggestions.length - 1 ? 0 : prev + 1
        )
        break
      case 'Escape':
        setShowSuggestions(false)
        setActiveSuggestionIndex(-1)
        break
      case 'Backspace':
        if (!inputValue && value.length > 0) {
          removeTag(value[value.length - 1]._id)
        }
        break
      case ',':
      case ';':
        e.preventDefault()
        if (inputValue.trim()) {
          addTag(inputValue.trim())
        }
        break
    }
  }

  // 태그 선택 함수 (클릭용)
  const selectTag = (tag: Tag, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    addTag(tag.name)
  }

  // 외부 클릭 시 자동완성 숨기기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const suggestionDropdown = document.querySelector(
        '[data-suggestions-dropdown]'
      )
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        (!suggestionDropdown ||
          !suggestionDropdown.contains(event.target as Node))
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative">
      {/* 태그 입력 영역 */}
      <div className="min-h-[42px] rounded-md border border-gray-300 bg-white p-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 dark:border-gray-600 dark:bg-gray-700">
        <div className="flex flex-wrap gap-1">
          {/* 선택된 태그들 */}
          {value.map(tag => (
            <span
              key={tag._id}
              className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
              style={{
                backgroundColor: `${tag.color}20`,
                color: tag.color,
                borderColor: tag.color + '40',
                border: '1px solid',
              }}
            >
              {tag.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag._id)}
                  className="ml-1 rounded-full p-0.5 hover:bg-black/10"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}

          {/* 입력 필드 */}
          {!disabled && value.length < maxTags && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => inputValue && setShowSuggestions(true)}
              placeholder={value.length === 0 ? placeholder : ''}
              className="min-w-[120px] flex-1 border-none bg-transparent text-sm placeholder-gray-400 outline-none dark:text-white"
            />
          )}
        </div>
      </div>

      {/* 자동완성 드롭다운 */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          data-suggestions-dropdown
          className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion._id}
              type="button"
              onMouseDown={e => selectTag(suggestion, e)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-600 ${
                index === activeSuggestionIndex
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : ''
              }`}
            >
              <span className="flex items-center">
                <div
                  className="mr-2 h-3 w-3 rounded"
                  style={{ backgroundColor: suggestion.color }}
                />
                <span className="text-sm text-gray-900 dark:text-white">
                  {suggestion.name}
                </span>
              </span>
              {suggestion.usageCount && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {suggestion.usageCount}회 사용
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* 도움말 텍스트 */}
      {!disabled && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Enter, 쉼표(,) 또는 세미콜론(;)으로 태그 추가 • 최대 {maxTags}개
        </p>
      )}
    </div>
  )
}

export default TagInput
